import { qpParseInt } from '@library/functions/math/qp-parse-int';
import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { ScriptLoaderService } from '@one/app/services/script-loader.service';
import { IAccount } from '@one/app/shared/models/account/account.models';
import { WINDOW } from '@one/app/shared/tokens/window.token';
import { inject, Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { QimaOptionalType } from '@qima/ngx-qima';
import { from, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';

declare let appcuesId;

@Injectable({
  providedIn: 'root',
})
export class AppcuesService extends ScriptLoaderService {
  public readonly _router = inject(Router);
  public readonly _window = inject(WINDOW);
  public readonly _qpLoggerService = inject(QpLoggerService);

  private readonly USER_DATA_SENT_TO_APPCUES: string[] = [
    'autoTranslationAvailable',
    'autoTranslationEnabled',
    'autoTranslationEnabledLanguages',
    'brandId',
    'clientName',
    'companyName',
    'createdBy',
    'createdDate',
    'email',
    'factoryId',
    'firstName',
    'id',
    'langKey',
    'lastName',
    'login',
    'position',
    'profiles',
    'role',
  ];

  private readonly id: QimaOptionalType<string> = this._window['appcuesId'] ?? appcuesId;

  public constructor() {
    super();

    if (this.id && !isNaN(qpParseInt(this.id))) {
      this.scriptUrl = `//fast.appcues.com/${this.id}.js`;
    }
  }

  public load$(userData: Partial<IAccount>): Observable<void> {
    if (!this.scriptUrl) {
      return of(undefined);
    }

    // Give only the values you need to a third party!
    const dataToSend = this._filterUserData(userData);

    return from(this.loadScript(this.scriptUrl)).pipe(
      switchMap((): Observable<Event> => this._router.events),
      filter((event): boolean => event instanceof NavigationEnd),
      tap((): void => {
        const appcuesIdentify = this._window['Appcues']?.identify;

        if (typeof appcuesIdentify === 'function') {
          try {
            appcuesIdentify(dataToSend.id, dataToSend);
          } catch (err) {
            this._qpLoggerService.debug('unable to identify with Appcues', err);
          }
        }
      }),
      map((): undefined => undefined),
      catchError((err): Observable<void> => {
        this._qpLoggerService.debug('unable to load Appcues', err);

        return of(undefined);
      })
    );
  }

  private _filterUserData(initialData: Partial<IAccount>): Partial<IAccount> {
    return Object.keys(initialData)
      .filter((dataKey): boolean => this.USER_DATA_SENT_TO_APPCUES.includes(dataKey))
      .reduce(
        (acc, dataKey): Partial<IAccount> => ({
          ...acc,
          [dataKey]: Array.isArray(initialData[dataKey]) ? initialData[dataKey].join(',') : initialData[dataKey],
        }),
        {}
      );
  }
}
